import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const DEFAULTS = {
  preset_key: "photomoto",
  site_name_primary: "Photo",
  site_name_accent: "Moto",
  site_domain: "photomoto.co.uk",
  tagline: "The one-stop shop for motorsport event photography — from motocross paddocks to circuit finishing straights. Photos from every photographer, in one place.",
  hero_line1: "Shot at the track.",
  hero_line2: "Sold in one place.",
  categories_strip: "Cars · Road Bikes · Off-Road · Motocross · Karts",
  calendar_label: "Race calendar",
  event_noun: "race weekend",
  vertical_noun: "motorsport",
  accent_color: "#FF6A00",
  contact_email: "hello@photomoto.co.uk",
};

const SiteConfigContext = createContext({ ...DEFAULTS, siteName: "PhotoMoto", refresh: () => {} });

function hexToRgb(hex) {
  const m = /^#([0-9a-f]{6})$/i.exec(hex || "");
  if (!m) return { r: 255, g: 106, b: 0 };
  const n = parseInt(m[1], 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

export function applyAccent(hex) {
  const { r, g, b } = hexToRgb(hex);
  const root = document.documentElement;
  root.style.setProperty("--pm-orange", hex);
  root.style.setProperty("--pm-orange-bright", `rgb(${Math.min(r + 32, 255)}, ${Math.min(g + 32, 255)}, ${Math.min(b + 32, 255)})`);
  root.style.setProperty("--pm-orange-20", `rgba(${r}, ${g}, ${b}, 0.2)`);
  root.style.setProperty("--pm-orange-30", `rgba(${r}, ${g}, ${b}, 0.3)`);
  root.style.setProperty("--pm-orange-40", `rgba(${r}, ${g}, ${b}, 0.4)`);
}

export function SiteConfigProvider({ children }) {
  const [config, setConfig] = useState(DEFAULTS);

  const refresh = () =>
    api.get("/public/site-config")
      .then((r) => setConfig({ ...DEFAULTS, ...r.data }))
      .catch(() => {});

  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch once on mount
  useEffect(() => { refresh(); }, []);

  useEffect(() => {
    applyAccent(config.accent_color);
    document.title = `${config.site_name_primary}${config.site_name_accent} — ${config.vertical_noun} event photography`;
  }, [config]);

  const siteName = `${config.site_name_primary}${config.site_name_accent}`;
  return (
    <SiteConfigContext.Provider value={{ ...config, siteName, refresh }}>
      {children}
    </SiteConfigContext.Provider>
  );
}

export function useSiteConfig() {
  return useContext(SiteConfigContext);
}
